/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  Directive, ElementRef, Renderer2, OnDestroy, OnInit, RendererStyleFlags2, Input,
} from '@angular/core';
import { fromEvent, Subject, Subscription } from 'rxjs';

/**
 * Directive used to calculate opacity on images as they come from below the viewport threshold.
 * The viewport threshold means the bottom of the viewport. The IntersectionObserver used is
 * set to fire instersection events when an image comes into view at the bottom of the viewport
 * Intersection events occur when the top of the image hits the bottom of the viewport as the image is
 * coming above the viewport threshold.
 */
@Directive({
  selector: '[bulbImageFade]',
  standalone: false,
})
export class ImageFadeDirective implements OnDestroy, OnInit {

  /**
   * Input to determine if the fade directive is initialized or not. The fade on images should only happen
   * when the current page the image exists on is not being edited.
   */
  @Input('bulbImageFade') isEditingPage: boolean;

  /**
   * Used to set the CSS Custom Variable on the image component
   */
  private imageOpacityPropName: string = '--bulb-image-onscroll-opacity';

  /**
   * Initial opacity should be 1 and will be modified as the user scrolls
   */
  private assignedOpacity: number = 1;

  /**
   * Intersection Observer to initialize callback upon intersection at 0% of observed element.
   * Default root of the observer is the window so intersection events occur when observed element intersects
   * the window at the elements 0% (Its top or when the element is just coming into view).
   */
  private imageThresholdObserver: IntersectionObserver = new IntersectionObserver(
    this.intersectionCallback.bind(this),
    { threshold: 0 },
  );

  /**
   * Subscription to window scroll events
   */
  private scrollListener: Subscription;

  /**
   * Subject to emit scroll events that are subscribed to in the intersectionCallback
   * There may be multiple subscribers since intersection can occur for each image component
   * on the page.
   */
  private scrollSubject: Subject<Event> = new Subject();

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    if (!this.isEditingPage) {
      this.imageThresholdObserver.observe(this.el.nativeElement);
      this.scrollListener = fromEvent(window, 'scroll').subscribe((event: Event) => {
        this.scrollSubject.next(event);
      });
    }
  }

  private intersectionCallback(event: IntersectionObserverEntry[]): void {
    event.forEach((e: IntersectionObserverEntry) => {
      if (e.isIntersecting) {
        this.scrollSubject.subscribe(() => {
          const windowHeight: number = window.innerHeight;
          const { height, top }: DOMRect = this.el.nativeElement.getBoundingClientRect();
          const viewOpacityRatio: number = 2.5;
          /**
           * viewPercentMultiplier adds a multiplier to the percentInView variables if 50% of the image height
           * is larger than 50% of the current windows innerHeight. This satisfies the requirement that the view
           * percentage of the window takes superiority over the current percentage of the image above/below the
           * viewport threshold.
           */
          const viewPercentMultiplier: number = height / 2 > windowHeight / 2 ? height / windowHeight : 1;
          // Percentage of the image that is currently in view from the bottom of the viewport
          const percentInViewBottom: number = Math.abs(((top - windowHeight) / height) * viewPercentMultiplier);

          this.assignedOpacity = percentInViewBottom < 0.1 ? 0 : 1;
          /**
           * Checks if the current percentage of percentInViewBottom is within the bounds
           * of 0 - 0.5 and if it is then we set the opacity supplied to the renderer which is set to a 2 : 1 ratio of
           * view percentage to opacity. We ignore the first 10% of the image so the opacity effect begins when more
           * 10% or more of the image is above the threshold. To account for this, the opacity multiplier is 2.5x
           * the view to account for ignoring the first 10% which is subtracted from the current percentInViewBottom
           * value.
           */
          if (percentInViewBottom < 0.5 && percentInViewBottom > 0) {
            this.assignedOpacity = ((percentInViewBottom - 0.1) * viewOpacityRatio);
          }
          const effectOpacity: string = String(this.assignedOpacity);

          this.renderer.setStyle(
            this.el.nativeElement,
            this.imageOpacityPropName,
            effectOpacity,
            RendererStyleFlags2.DashCase,
          );
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.scrollSubject?.unsubscribe();
    this.scrollListener?.unsubscribe();
    this.imageThresholdObserver.disconnect();
  }
}
